import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  Timestamp
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';

export const ACCOUNTING_COLLECTION = 'accountingEntries';

/** Chart of accounts (MB, ne PVM mokėtojas) — codes are labels for the official journal. */
export const ACCOUNTS = {
  BANK: { code: '2710', name: 'Bankas' },
  RECEIVABLE: { code: '2410', name: 'Gautinos sumos (klientai)' },
  ADVANCES: { code: '4490', name: 'Gauti avansai (skola klientams)' },
  PAYABLE: { code: '4430', name: 'Mokėtinos sumos (tiekėjai)' },
  REVENUE: { code: '5000', name: 'Pajamos (paslaugos)' },
  EXPENSE: { code: '6000', name: 'Sąnaudos' },
  OTHER: { code: '3900', name: 'Kita / kapitalas' }
};

export const ENTRY_TYPES = {
  SALE: 'sale',
  PAYMENT_RECEIVED: 'payment_received',
  ADVANCE_RECEIVED: 'advance_received',
  ADVANCE_APPLIED: 'advance_applied',
  PURCHASE: 'purchase',
  SUPPLIER_PAYMENT: 'supplier_payment',
  OTHER: 'other'
};

export const ENTRY_TYPE_LABELS = {
  [ENTRY_TYPES.SALE]: 'Pardavimas (SF)',
  [ENTRY_TYPES.PAYMENT_RECEIVED]: 'Gautas mokėjimas',
  [ENTRY_TYPES.ADVANCE_RECEIVED]: 'Gautas avansas',
  [ENTRY_TYPES.ADVANCE_APPLIED]: 'Avanso užskaitymas',
  [ENTRY_TYPES.PURCHASE]: 'Pirkimas / sąnaudos',
  [ENTRY_TYPES.SUPPLIER_PAYMENT]: 'Mokėjimas tiekėjui',
  [ENTRY_TYPES.OTHER]: 'Kita'
};

const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * Prefer email so the same person is one row even when some SF have clientId
 * and others only have email (common cause of duplicate Klientų balansas rows).
 */
export const normalizeClientKey = ({ clientId, clientEmail } = {}) => {
  const email = String(clientEmail || '').trim().toLowerCase();
  if (email.includes('@')) return `email:${email}`;
  const id = String(clientId || '').trim();
  if (id) return `id:${id}`;
  return '';
};

/**
 * Client balance sign convention (UI):
 *   +EUR = MB skolinga klientui (avansas / permoka)
 *   -EUR = klientas skolingas MB (neapmokėta SF)
 */
export const clientBalanceDelta = (entry) => {
  const amount = Number(entry?.amountEur || 0);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  switch (entry?.type) {
    case ENTRY_TYPES.SALE:
      return -amount; // klientas skolingas
    case ENTRY_TYPES.PAYMENT_RECEIVED:
      return +amount; // skola klientui sumažėja (pinigai)
    case ENTRY_TYPES.ADVANCE_RECEIVED:
      return +amount; // MB skolinga klientui
    case ENTRY_TYPES.ADVANCE_APPLIED:
      // Užskaito avansą į jau registruotą SF (Dr avansai / Cr gautinos) — grynasis likutis 0
      return 0;
    default:
      return 0;
  }
};

export const formatClientBalance = (balance) => {
  const n = Number(balance || 0);
  const abs = Math.abs(n).toFixed(2);
  if (n > 0) return `+${abs} Eur`;
  if (n < 0) return `-${abs} Eur`;
  return `0.00 Eur`;
};

export const saleEntryId = (year, invoiceId) => `sale_${year}_${invoiceId}`;
export const paymentEntryId = (year, invoiceId) => `pay_${year}_${invoiceId}`;
export const advanceFromPreinvoiceEntryId = (year, preinvoiceId) => `adv_${year}_${preinvoiceId}`;

const accountPairForType = (type) => {
  switch (type) {
    case ENTRY_TYPES.SALE:
      return { debit: ACCOUNTS.RECEIVABLE, credit: ACCOUNTS.REVENUE };
    case ENTRY_TYPES.PAYMENT_RECEIVED:
      return { debit: ACCOUNTS.BANK, credit: ACCOUNTS.RECEIVABLE };
    case ENTRY_TYPES.ADVANCE_RECEIVED:
      return { debit: ACCOUNTS.BANK, credit: ACCOUNTS.ADVANCES };
    case ENTRY_TYPES.ADVANCE_APPLIED:
      // Settles existing SF receivable from client advance (revenue already booked on SALE)
      return { debit: ACCOUNTS.ADVANCES, credit: ACCOUNTS.RECEIVABLE };
    case ENTRY_TYPES.PURCHASE:
      return { debit: ACCOUNTS.EXPENSE, credit: ACCOUNTS.PAYABLE };
    case ENTRY_TYPES.SUPPLIER_PAYMENT:
      return { debit: ACCOUNTS.PAYABLE, credit: ACCOUNTS.BANK };
    case ENTRY_TYPES.OTHER:
    default:
      return { debit: ACCOUNTS.OTHER, credit: ACCOUNTS.BANK };
  }
};

const buildEntryPayload = ({
  type,
  date,
  documentNumber,
  description,
  amountEur,
  clientId = '',
  clientEmail = '',
  clientName = '',
  counterpartyName = '',
  source = 'manual',
  invoiceId = '',
  invoiceYear = null,
  preinvoiceId = '',
  pdfUrl = '',
  pdfStoragePath = '',
  isTest = false,
  expenseCategory = ''
}) => {
  const amount = Number(amountEur);
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error('Neteisinga suma');
  }
  if (!String(date || '').trim()) {
    throw new Error('Trūksta datos');
  }
  const pair = accountPairForType(type);
  // OTHER with positive "expense-like" default: Dr OTHER / Cr BANK (money out).
  // Caller may override via type-specific helpers.
  return {
    date: String(date).trim().slice(0, 10),
    type,
    typeLabel: ENTRY_TYPE_LABELS[type] || type,
    documentNumber: String(documentNumber || '').trim(),
    description: String(description || '').trim(),
    amountEur: Math.round(amount * 100) / 100,
    clientId: String(clientId || '').trim(),
    clientEmail: String(clientEmail || '').trim().toLowerCase(),
    clientName: String(clientName || '').trim(),
    counterpartyName: String(counterpartyName || clientName || '').trim(),
    clientKey: normalizeClientKey({ clientId, clientEmail }),
    debitAccount: pair.debit.code,
    debitAccountName: pair.debit.name,
    creditAccount: pair.credit.code,
    creditAccountName: pair.credit.name,
    source,
    invoiceId: String(invoiceId || '').trim(),
    invoiceYear: invoiceYear != null ? Number(invoiceYear) : null,
    preinvoiceId: String(preinvoiceId || '').trim(),
    pdfUrl: String(pdfUrl || '').trim(),
    pdfStoragePath: String(pdfStoragePath || '').trim(),
    expenseCategory: String(expenseCategory || '').trim(),
    isTest: isTest === true,
    affectsClientBalance: [
      ENTRY_TYPES.SALE,
      ENTRY_TYPES.PAYMENT_RECEIVED,
      ENTRY_TYPES.ADVANCE_RECEIVED
    ].includes(type),
    updatedAt: Timestamp.now()
  };
};

export const upsertAccountingEntry = async (entryId, fields, { isNew = false } = {}) => {
  const payload = buildEntryPayload(fields);
  const refDoc = doc(db, ACCOUNTING_COLLECTION, entryId);
  if (isNew) {
    await setDoc(refDoc, { ...payload, createdAt: Timestamp.now() });
  } else {
    const snap = await getDoc(refDoc);
    if (snap.exists()) {
      await setDoc(refDoc, { ...payload, createdAt: snap.data().createdAt || Timestamp.now() }, { merge: true });
    } else {
      await setDoc(refDoc, { ...payload, createdAt: Timestamp.now() });
    }
  }
  return entryId;
};

export const createManualAccountingEntry = async (fields) => {
  const id = doc(collection(db, ACCOUNTING_COLLECTION)).id;
  await upsertAccountingEntry(id, { ...fields, source: fields.source || 'manual' }, { isNew: true });
  return id;
};

/**
 * Manual balance adjustment from Klientų balansas.
 * Positive amount → +credit (gauti pinigai). Negative → −credit (korekcija / paslaugos).
 */
export const adjustClientBalanceManually = async ({
  clientId = '',
  clientEmail = '',
  clientName = '',
  amountEur,
  date,
  note = ''
}) => {
  const amount = Number(amountEur);
  if (!Number.isFinite(amount) || amount === 0) {
    throw new Error('Įveskite sumą (ne nulį)');
  }
  const d = String(date || todayIso()).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    throw new Error('Neteisinga data');
  }
  const key = normalizeClientKey({ clientId, clientEmail });
  if (!key) {
    throw new Error('Trūksta kliento el. pašto arba ID');
  }

  const abs = Math.round(Math.abs(amount) * 100) / 100;
  const name = String(clientName || '').trim();
  const isCredit = amount > 0;

  return createManualAccountingEntry({
    type: isCredit ? ENTRY_TYPES.ADVANCE_RECEIVED : ENTRY_TYPES.SALE,
    date: d,
    documentNumber: 'RANKINIS',
    description: [
      isCredit ? 'Rankinis balanso didinimas (gauti pinigai)' : 'Rankinis balanso mažinimas',
      note ? String(note).trim() : ''
    ].filter(Boolean).join(' — '),
    amountEur: abs,
    clientId: String(clientId || '').trim(),
    clientEmail: String(clientEmail || '').trim().toLowerCase(),
    clientName: name,
    counterpartyName: name,
    source: 'balance_manual_ui'
  });
};

/**
 * Record money received from a client (wallet +credit).
 * Not tied as a full settlement of one SF — amount may differ from invoice total.
 * @param {'invoice'|'preinvoice'} listType
 */
export const recordClientMoneyReceived = async ({
  listType,
  item,
  year,
  amountEur,
  date,
  note = ''
}) => {
  const amount = Number(amountEur);
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error('Neteisinga gauta suma');
  }
  const d = String(date || todayIso()).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    throw new Error('Neteisinga data');
  }
  if (!item?.id) {
    throw new Error('Trūksta dokumento');
  }

  const isPreinvoice = listType === 'preinvoice';
  const docNo = item.invoiceNumber || item.id;
  const clientName = item.invoiceForCompany && item.companyName
    ? item.companyName
    : (item.clientName || '');
  const descParts = [
    `Gauti pinigai pagal ${isPreinvoice ? 'išankstinę' : 'SF'} ${docNo}`,
    note ? String(note).trim() : ''
  ].filter(Boolean);

  return createManualAccountingEntry({
    type: ENTRY_TYPES.ADVANCE_RECEIVED,
    date: d,
    documentNumber: docNo,
    description: descParts.join(' — '),
    amountEur: amount,
    clientId: item.clientId || '',
    clientEmail: item.clientEmail || '',
    clientName,
    counterpartyName: clientName,
    source: 'money_received_ui',
    invoiceId: isPreinvoice ? '' : item.id,
    invoiceYear: year != null ? Number(year) : null,
    preinvoiceId: isPreinvoice ? item.id : '',
    isTest: item.isTest === true
  });
};

export const updateAccountingEntryDate = async (entryId, date) => {
  const d = String(date || '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    throw new Error('Neteisinga data');
  }
  await updateDoc(doc(db, ACCOUNTING_COLLECTION, entryId), {
    date: d,
    updatedAt: Timestamp.now()
  });
};

export const updateAccountingEntryFields = async (entryId, patch) => {
  const allowed = {};
  if (patch.date != null) allowed.date = String(patch.date).trim().slice(0, 10);
  if (patch.description != null) allowed.description = String(patch.description).trim();
  if (patch.documentNumber != null) allowed.documentNumber = String(patch.documentNumber).trim();
  if (patch.amountEur != null) {
    const amount = Number(patch.amountEur);
    if (!Number.isFinite(amount) || amount <= 0) throw new Error('Neteisinga suma');
    allowed.amountEur = Math.round(amount * 100) / 100;
  }
  if (patch.counterpartyName != null) allowed.counterpartyName = String(patch.counterpartyName).trim();
  allowed.updatedAt = Timestamp.now();
  await updateDoc(doc(db, ACCOUNTING_COLLECTION, entryId), allowed);
};

/** Delete journal entry only — never touches invoices / preinvoices. */
export const deleteAccountingEntry = async (entryId, { deletePdf = true } = {}) => {
  const refDoc = doc(db, ACCOUNTING_COLLECTION, entryId);
  const snap = await getDoc(refDoc);
  if (!snap.exists()) return;
  const data = snap.data() || {};
  if (deletePdf && data.pdfStoragePath) {
    try {
      await deleteObject(ref(storage, data.pdfStoragePath));
    } catch (err) {
      if (err?.code !== 'storage/object-not-found') {
        console.warn('Skipping accounting PDF delete:', err?.code || err?.message || err);
      }
    }
  }
  await deleteDoc(refDoc);
};

export const ensureSaleEntryFromInvoice = async (invoice, year) => {
  const invoiceId = invoice?.id;
  if (!invoiceId) return null;
  if (invoice?.status === 'sendError') return null;
  const amount = Number(invoice.amountEur || 0);
  if (!amount) return null;

  const y = year || Number(String(invoice.invoiceDate || '').slice(0, 4)) || new Date().getFullYear();
  const id = saleEntryId(y, invoiceId);
  await upsertAccountingEntry(id, {
    type: ENTRY_TYPES.SALE,
    date: invoice.invoiceDate || todayIso(),
    documentNumber: invoice.invoiceNumber || '',
    description: invoice.serviceDescription
      ? String(invoice.serviceDescription)
      : `Sąskaita faktūra ${invoice.invoiceNumber || invoiceId}`,
    amountEur: amount,
    clientId: invoice.clientId || '',
    clientEmail: invoice.clientEmail || '',
    clientName: invoice.invoiceForCompany && invoice.companyName
      ? invoice.companyName
      : (invoice.clientName || ''),
    counterpartyName: invoice.invoiceForCompany && invoice.companyName
      ? invoice.companyName
      : (invoice.clientName || ''),
    source: invoice._migration ? 'migration' : 'invoice',
    invoiceId,
    invoiceYear: y,
    isTest: invoice.isTest === true
  });
  return id;
};

/**
 * When SF is marked Payed:
 * - Wallet model: money-in (išankstinė Payed / „Gautas avansas“) already increased balance.
 * - SALE already decreased balance for services.
 * - If balance after sale is >= 0, SF was covered by credit → ADVANCE_APPLIED (no second money-in).
 * - If balance < 0, client still owed → PAYMENT_RECEIVED (real bank payment for this SF).
 */
export const ensurePaymentEntryFromInvoice = async (invoice, year, { paymentDate, forceType } = {}) => {
  const invoiceId = invoice?.id;
  if (!invoiceId) return null;
  const amount = Number(invoice.amountEur || 0);
  if (!amount) return null;

  const y = year || Number(String(invoice.invoiceDate || '').slice(0, 4)) || new Date().getFullYear();
  // Sale must exist first so balance = money_in - services includes this SF.
  await ensureSaleEntryFromInvoice(invoice, y);

  const id = paymentEntryId(y, invoiceId);
  const clientKey = normalizeClientKey(invoice);

  let type = forceType || ENTRY_TYPES.PAYMENT_RECEIVED;
  if (!forceType && clientKey) {
    const balance = await computeClientBalance(clientKey, {
      excludeEntryIds: [id],
      clientId: invoice.clientId || '',
      clientEmail: invoice.clientEmail || ''
    });
    // After SALE is posted: 0 or + means credit covered this SF (even when exactly equal).
    // Old bug used `balance >= amount`, which failed when leftover credit was 0.
    if (balance >= -0.001) {
      type = ENTRY_TYPES.ADVANCE_APPLIED;
    }
  }

  await upsertAccountingEntry(id, {
    type,
    date: paymentDate || todayIso(),
    documentNumber: invoice.invoiceNumber || '',
    description: type === ENTRY_TYPES.ADVANCE_APPLIED
      ? `Avanso užskaitymas už ${invoice.invoiceNumber || invoiceId}`
      : `Mokėjimas už ${invoice.invoiceNumber || invoiceId}`,
    amountEur: amount,
    clientId: invoice.clientId || '',
    clientEmail: invoice.clientEmail || '',
    clientName: invoice.invoiceForCompany && invoice.companyName
      ? invoice.companyName
      : (invoice.clientName || ''),
    counterpartyName: invoice.invoiceForCompany && invoice.companyName
      ? invoice.companyName
      : (invoice.clientName || ''),
    source: invoice._migration ? 'migration' : 'invoice_payment',
    invoiceId,
    invoiceYear: y,
    isTest: invoice.isTest === true
  });
  return id;
};

export const removePaymentEntryForInvoice = async (year, invoiceId) => {
  const y = year || new Date().getFullYear();
  const id = paymentEntryId(y, invoiceId);
  const snap = await getDoc(doc(db, ACCOUNTING_COLLECTION, id));
  if (snap.exists()) {
    await deleteDoc(doc(db, ACCOUNTING_COLLECTION, id));
  }
};

export const ensureAdvanceEntryFromPreinvoice = async (preinvoice, year, { paymentDate } = {}) => {
  const preinvoiceId = preinvoice?.id;
  if (!preinvoiceId) return null;
  const amount = Number(preinvoice.amountEur || 0);
  if (!amount) return null;
  const y = year || Number(String(preinvoice.invoiceDate || '').slice(0, 4)) || new Date().getFullYear();
  const id = advanceFromPreinvoiceEntryId(y, preinvoiceId);
  await upsertAccountingEntry(id, {
    type: ENTRY_TYPES.ADVANCE_RECEIVED,
    date: paymentDate || preinvoice.invoiceDate || todayIso(),
    documentNumber: preinvoice.invoiceNumber || '',
    description: `Avansas pagal išankstinę ${preinvoice.invoiceNumber || preinvoiceId}`,
    amountEur: amount,
    clientId: preinvoice.clientId || '',
    clientEmail: preinvoice.clientEmail || '',
    clientName: preinvoice.invoiceForCompany && preinvoice.companyName
      ? preinvoice.companyName
      : (preinvoice.clientName || ''),
    counterpartyName: preinvoice.invoiceForCompany && preinvoice.companyName
      ? preinvoice.companyName
      : (preinvoice.clientName || ''),
    source: 'preinvoice_payment',
    preinvoiceId,
    invoiceYear: y,
    isTest: preinvoice.isTest === true
  });
  return id;
};

export const removeAdvanceEntryForPreinvoice = async (year, preinvoiceId) => {
  const id = advanceFromPreinvoiceEntryId(year, preinvoiceId);
  const snap = await getDoc(doc(db, ACCOUNTING_COLLECTION, id));
  if (snap.exists()) {
    await deleteDoc(doc(db, ACCOUNTING_COLLECTION, id));
  }
};

/**
 * Sync Apskaita when SF status changes. Never modifies/deletes the invoice document itself
 * beyond what the caller already does.
 */
export const syncAccountingForInvoiceStatus = async (year, invoice, previousStatus, nextStatus) => {
  if (!invoice?.id) return;
  if (nextStatus === 'sendError') return;

  await ensureSaleEntryFromInvoice(invoice, year);

  if (nextStatus === 'Payed' && previousStatus !== 'Payed') {
    await ensurePaymentEntryFromInvoice(invoice, year, { paymentDate: todayIso() });
  } else if (previousStatus === 'Payed' && nextStatus !== 'Payed') {
    await removePaymentEntryForInvoice(year, invoice.id);
  }
};

export const syncAccountingForPreinvoiceStatus = async (year, preinvoice, previousStatus, nextStatus) => {
  if (!preinvoice?.id) return;
  if (nextStatus === 'Payed' && previousStatus !== 'Payed') {
    await ensureAdvanceEntryFromPreinvoice(preinvoice, year, { paymentDate: todayIso() });
  } else if (previousStatus === 'Payed' && nextStatus !== 'Payed') {
    await removeAdvanceEntryForPreinvoice(year, preinvoice.id);
  }
};

export const subscribeToAccountingEntries = (callback) => {
  const q = query(collection(db, ACCOUNTING_COLLECTION), orderBy('date', 'desc'), limit(2000));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, (error) => {
    console.error('accountingEntries snapshot error:', error);
    callback([]);
  });
};

export const listAccountingEntriesInPeriod = async (dateFrom, dateTo) => {
  const col = collection(db, ACCOUNTING_COLLECTION);
  let q;
  if (dateFrom && dateTo) {
    q = query(col, where('date', '>=', dateFrom), where('date', '<=', dateTo), orderBy('date', 'asc'));
  } else {
    q = query(col, orderBy('date', 'asc'), limit(5000));
  }
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const computeClientBalance = async (clientKey, {
  excludeEntryIds = [],
  clientId = '',
  clientEmail = ''
} = {}) => {
  const email = String(clientEmail || '').trim().toLowerCase();
  const id = String(clientId || '').trim();
  const exclude = new Set(excludeEntryIds);
  const seen = new Set();
  let balance = 0;

  const addSnap = (snap) => {
    snap.docs.forEach((d) => {
      if (exclude.has(d.id) || seen.has(d.id)) return;
      seen.add(d.id);
      const data = d.data() || {};
      if (data.affectsClientBalance === false) return;
      balance += clientBalanceDelta(data);
    });
  };

  // Match both legacy id:* keys and email:* keys for the same person.
  if (email.includes('@')) {
    const qEmail = query(
      collection(db, ACCOUNTING_COLLECTION),
      where('clientEmail', '==', email),
      limit(2000)
    );
    addSnap(await getDocs(qEmail));
  }
  if (id) {
    const qId = query(
      collection(db, ACCOUNTING_COLLECTION),
      where('clientId', '==', id),
      limit(2000)
    );
    addSnap(await getDocs(qId));
  }
  if (!email.includes('@') && !id && clientKey) {
    const qKey = query(
      collection(db, ACCOUNTING_COLLECTION),
      where('clientKey', '==', clientKey),
      limit(2000)
    );
    addSnap(await getDocs(qKey));
  }

  return Math.round(balance * 100) / 100;
};

export const buildClientBalancesFromEntries = (entries, { includeZeroFromInvoices = [] } = {}) => {
  const map = new Map();

  const touch = (key, patch) => {
    if (!key) return;
    const prev = map.get(key) || {
      clientKey: key,
      clientId: '',
      clientEmail: '',
      clientName: '',
      balance: 0
    };
    map.set(key, {
      ...prev,
      ...patch,
      clientId: patch.clientId || prev.clientId,
      clientEmail: patch.clientEmail || prev.clientEmail,
      clientName: patch.clientName || prev.clientName,
      balance: patch.balance != null ? patch.balance : prev.balance
    });
  };

  (entries || []).forEach((e) => {
    if (!e.affectsClientBalance) return;
    // Always regroup by email when present (ignores legacy stored clientKey id:…)
    const key = normalizeClientKey(e);
    if (!key) return;
    const prev = map.get(key);
    const balance = Math.round(((prev?.balance || 0) + clientBalanceDelta(e)) * 100) / 100;
    touch(key, {
      clientId: e.clientId,
      clientEmail: e.clientEmail,
      clientName: e.clientName || e.counterpartyName,
      balance
    });
  });

  (includeZeroFromInvoices || []).forEach((inv) => {
    const key = normalizeClientKey(inv);
    if (!key || map.has(key)) return;
    touch(key, {
      clientId: inv.clientId || '',
      clientEmail: inv.clientEmail || '',
      clientName: inv.invoiceForCompany && inv.companyName ? inv.companyName : (inv.clientName || ''),
      balance: 0
    });
  });

  return [...map.values()].sort((a, b) => {
    if (a.balance !== b.balance) return a.balance - b.balance;
    return String(a.clientName || a.clientEmail).localeCompare(String(b.clientName || b.clientEmail), 'lt');
  });
};

const listInvoiceItemsForYear = async (year) => {
  const snap = await getDocs(collection(db, 'invoices', String(year), 'items'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data(), _year: year }));
};

/** List invoices across years. Does not delete or modify invoice docs. */
export const listAllInvoicesForMigration = async ({ fromYear = 2024, toYear } = {}) => {
  const end = toYear || new Date().getFullYear();
  const all = [];
  for (let y = fromYear; y <= end; y += 1) {
    try {
      const items = await listInvoiceItemsForYear(y);
      all.push(...items.map((i) => ({ ...i, _year: y })));
    } catch (err) {
      console.warn(`Skip invoices year ${y}:`, err?.message || err);
    }
  }
  return all;
};

/**
 * Safe backfill: CREATE/UPDATE accounting entries only.
 * Never deletes or updates invoice / preinvoice documents.
 */
export const migrateInvoicesToAccounting = async ({
  fromYear = 2024,
  toYear,
  onProgress
} = {}) => {
  const invoices = await listAllInvoicesForMigration({ fromYear, toYear });
  let saleCount = 0;
  let paymentCount = 0;
  let skipped = 0;

  for (let i = 0; i < invoices.length; i += 1) {
    const inv = { ...invoices[i], _migration: true };
    const year = inv._year;
    onProgress?.({ index: i + 1, total: invoices.length, invoiceNumber: inv.invoiceNumber });

    if (inv.status === 'sendError' || !Number(inv.amountEur)) {
      skipped += 1;
      continue;
    }

    await ensureSaleEntryFromInvoice(inv, year);
    saleCount += 1;

    if (inv.status === 'Payed') {
      await ensurePaymentEntryFromInvoice(inv, year, {
        paymentDate: inv.invoiceDate || todayIso(),
        forceType: ENTRY_TYPES.PAYMENT_RECEIVED
      });
      paymentCount += 1;
    }
  }

  return {
    invoiceCount: invoices.length,
    saleCount,
    paymentCount,
    skipped,
    note: 'Sąskaitos faktūros Firebase kolekcijoje nebuvo trinamos ar keičiamos.'
  };
};

export const uploadPurchasePdf = async (file, entryId) => {
  if (!file) throw new Error('Pasirinkite PDF failą');
  const safeName = String(file.name || 'pirkimas.pdf').replace(/[^\w.\-ąčęėįšųūžĄČĘĖĮŠŲŪŽ]+/gi, '_');
  const path = `accounting/${new Date().getFullYear()}/${entryId}_${safeName}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, { contentType: file.type || 'application/pdf' });
  const pdfUrl = await getDownloadURL(storageRef);
  return { pdfUrl, pdfStoragePath: path };
};

export const createPurchaseEntryWithPdf = async ({
  date,
  documentNumber,
  description,
  amountEur,
  counterpartyName,
  paidImmediately = false,
  file
}) => {
  const id = doc(collection(db, ACCOUNTING_COLLECTION)).id;
  let pdfUrl = '';
  let pdfStoragePath = '';
  if (file) {
    const uploaded = await uploadPurchasePdf(file, id);
    pdfUrl = uploaded.pdfUrl;
    pdfStoragePath = uploaded.pdfStoragePath;
  }

  await upsertAccountingEntry(id, {
    type: ENTRY_TYPES.PURCHASE,
    date,
    documentNumber,
    description,
    amountEur,
    counterpartyName,
    clientName: counterpartyName,
    source: 'manual',
    pdfUrl,
    pdfStoragePath
  }, { isNew: true });

  let paymentId = null;
  if (paidImmediately) {
    paymentId = `${id}_pay`;
    await upsertAccountingEntry(paymentId, {
      type: ENTRY_TYPES.SUPPLIER_PAYMENT,
      date,
      documentNumber,
      description: `Apmokėjimas: ${description || documentNumber || counterpartyName}`,
      amountEur,
      counterpartyName,
      source: 'manual'
    }, { isNew: true });
  }

  return { purchaseId: id, paymentId, pdfUrl };
};

/** Open a printable HTML journal for the period (browser → Save as PDF). */
export const openAccountingJournalPrint = (entries, { dateFrom, dateTo, title = 'Apskaitos žurnalas' } = {}) => {
  const rows = [...(entries || [])].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  const periodLabel = dateFrom && dateTo ? `${dateFrom} – ${dateTo}` : 'Visas laikotarpis';
  const escape = (s) => String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const bodyRows = rows.map((e, idx) => `
    <tr>
      <td>${idx + 1}</td>
      <td>${escape(e.date)}</td>
      <td>${escape(e.documentNumber)}</td>
      <td>${escape(e.typeLabel || e.type)}</td>
      <td>${escape(e.counterpartyName || e.clientName)}</td>
      <td>${escape(e.description)}</td>
      <td>${escape(e.debitAccount)} ${escape(e.debitAccountName)}</td>
      <td>${escape(e.creditAccount)} ${escape(e.creditAccountName)}</td>
      <td style="text-align:right">${Number(e.amountEur || 0).toFixed(2)}</td>
    </tr>
  `).join('');

  const html = `<!DOCTYPE html><html lang="lt"><head><meta charset="utf-8"/>
    <title>${escape(title)}</title>
    <style>
      body { font-family: DejaVu Sans, Arial, sans-serif; font-size: 11px; color: #111; margin: 24px; }
      h1 { font-size: 18px; margin: 0 0 4px; }
      .meta { color: #444; margin-bottom: 16px; }
      table { width: 100%; border-collapse: collapse; }
      th, td { border: 1px solid #999; padding: 4px 6px; vertical-align: top; }
      th { background: #eee; text-align: left; }
      @media print { body { margin: 12px; } }
    </style>
  </head><body>
    <h1>${escape(title)}</h1>
    <div class="meta">Laikotarpis: ${escape(periodLabel)} · Sugeneruota: ${escape(todayIso())} · Įrašų: ${rows.length}</div>
    <table>
      <thead>
        <tr>
          <th>Nr.</th><th>Data</th><th>Dok. nr.</th><th>Tipas</th><th>Kontrahentas</th>
          <th>Aprašymas</th><th>Debetas</th><th>Kreditas</th><th>Suma Eur</th>
        </tr>
      </thead>
      <tbody>${bodyRows || '<tr><td colspan="9">Nėra įrašų</td></tr>'}</tbody>
    </table>
    <script>window.onload = function () { window.print(); }</script>
  </body></html>`;

  const w = window.open('', '_blank');
  if (!w) throw new Error('Nepavyko atidaryti spausdinimo lango (popup blocked)');
  w.document.open();
  w.document.write(html);
  w.document.close();
};
